import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyEditToRow,
  CostChangeValue,
  hasEditablePricing,
  PricingAccountRow,
  PricingGroupRow,
  usePricingBookmarkRowsTransformer,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  BAR_CONTROL_HEIGHT,
  PricingBulkEditBottomBar,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-bulk-edit-bottom-bar';
import {
  BOOKMARKED_SCOPE,
  scopeStorageKey,
  usePricingEditsStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import {
  bookmarkKey,
  selectBookmarkCount,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import {
  BookmarkPair,
  useBookmarkActionForPairs,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bookmark-action';
import {
  buildAccountKey,
  splitAccountKey,
} from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { buildPricingFilterPredicate } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { buildPricingFilterSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-sections';
import {
  BulkEditPivot,
  useBulkEditingCore,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-editing-core';
import { Button, ButtonVariant } from '@sales-portal/components/button';
import { Checkbox, Tooltip } from '@usepepper/sprout-kit-ui';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnDef,
  createColumnHelper,
  Row,
  SortingState,
} from '@tanstack/react-table';
import {
  ColumnId,
  PricingGroupedTableRow,
} from '@sales-portal/domain/pricing/screens/management/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import { deriveCategoryOptions } from '@sales-portal/domain/pricing/screens/management/blocs/utils/category-filter';
import {
  emptyDelta,
  formatOrEmpty,
  isEmptyDisplayValue,
  valueOrNull,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/missing-value-display';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatBookmarkedDate,
  latestBookmarkedAt,
} from '@sales-portal/domain/pricing/screens/bookmarks/blocs/utils/bookmarked-date';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import {
  getHeaderLabel,
  PricingTableHeader,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header';
import {
  getLastCostInMicros,
  getLastGrossMarginPercentage,
  getLastPriceInMicros,
  percentOfPrevious,
  roundPercentageToNearestTenth,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { getRenderedRowIndex } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-utils';
import {
  groupedRowKey,
  usePricingSortSnapshot,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-sort-snapshot';
import {
  isDeltaColumn,
  isLeftAlignedColumn,
  PricingReviewFilterValue,
  pricingReviewGlobalFilterFn,
  resolveDelta,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { ItemInputCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-input-cell';
import { ItemUpdatedPricingCellContainer } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-updated-pricing-cell-container';
import { LayoutChangeEvent, View } from 'react-native';
import {
  leafDimensionOf,
  leafLabelOf,
  PricingGroupBy,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBookmarkGroupRow } from '@sales-portal/domain/pricing/screens/bookmarks/components/pricing-bookmark-group-row';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import {
  PricingEdit,
  PricingEditField,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingEditsBadge } from '@sales-portal/domain/pricing/components/pricing-edits-badge';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingGroupByToggleButton } from '@sales-portal/domain/pricing/components/pricing-group-by-toggle-button';
import { PricingItemDetailsTableRowContainer } from '@sales-portal/domain/pricing/screens/management/blocs/containers/pricing-item-details-table-row-container';
import { PricingPeerRangeCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/pricing-peer-range-cell';
import { PricingReviewButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingSelectAllRow } from '@sales-portal/domain/pricing/screens/management/components/pricing-select-all-row';
import { PricingTableDeltaCell } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableHeaderCells } from '@sales-portal/domain/pricing/components/pricing-table-header-cells';
import { PricingTableInputPlaceholder } from '@sales-portal/domain/pricing/screens/customers/components/pricing-table-input-placeholder';
import { PricingTableNameLink } from '@sales-portal/domain/pricing/components/pricing-table-name-link';
import { PricingTableSkeleton } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { PricingUndoButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-undo-button';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  SearchInput,
  SearchInputRef,
} from '@sales-portal/components/search-input';
import { SearchSkeleton } from '@/experiences/sales-portal/components/search-skeleton';
import { StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBulkQuickActions } from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-quick-actions';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { usePricingBookmarksLoader } from '@sales-portal/domain/pricing/blocs/loaders/use-pricing-bookmarks-loader';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingGroupBy } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-group-by';
import { usePricingSearch } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { useRowSelection } from '@sales-portal/domain/pricing/blocs/hooks/use-row-selection';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';

const columnHelper = createColumnHelper<PricingGroupedTableRow>();

const getRowKey = (row: PricingGroupedTableRow): string =>
  buildAccountKey(row as PricingAccountRow);

const isRowLocked = (row: PricingGroupedTableRow): boolean =>
  Boolean((row as PricingAccountRow).isLocked);

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingBookmarked
>;

export const PricingBookmarksScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const [styles, theme] = useStyles();
  const isFocused = useIsFocused();
  const {
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    isPeerRangeEnabled,
  } = useBusinessOrganizationPricingConfig();
  const { currencyCode, timezone, timezoneLoading } =
    usePricingEditsTimezoneSync();
  const edits = useFocusedPricingEditsStore((state) => state.edits);
  const { groupBy, setGroupBy } = usePricingGroupBy(TableName.PricingBookmarks);

  const toggleGroupBy = useCallback(() => {
    const next = leafDimensionOf(groupBy);
    setGroupBy(next);
    pricingManagementAnalytics.track(PricingAnalyticEvent.GROUP_BY_TOGGLE, {
      groupBy: next,
      pageSource: PricingPageSource.BOOKMARKED,
    });
  }, [groupBy, setGroupBy]);

  const navigateToEntity = useCallback(
    (dimension: PricingGroupBy, uuid: string) => {
      if (dimension === PricingGroupBy.VariantPack) {
        navigation.navigate(SALES_PORTAL_ROUTES.PricingItemManagement, {
          variantPackUUID: uuid,
        });
      } else {
        navigation.navigate(SALES_PORTAL_ROUTES.PricingCustomerDetail, {
          restaurantUUID: uuid,
        });
      }
    },
    [navigation],
  );

  const hasUndoHistory = usePricingEditsStore(
    (state) =>
      (state.editSnapshotStack[scopeStorageKey(BOOKMARKED_SCOPE)]?.length ??
        0) > 0,
  );
  const undo = usePricingEditsStore((state) => state.undo);
  const resetUndoHistory = usePricingEditsStore(
    (state) => state.resetUndoHistory,
  );
  const handleUndo = useCallback(() => undo(BOOKMARKED_SCOPE), [undo]);

  const { bookmarks, count, data, error, loading, refetch } =
    usePricingBookmarksLoader({
      revalidate: true,
    });

  // Once per mount, like the other pricing load events — the focus refetch
  // cycles `loading` again on every return to the tab.
  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_BOOKMARKED_PAGE,
        {},
      );
    }
  }, [data, error, loading]);

  const dataToDisplay = usePricingBookmarkRowsTransformer(bookmarks, groupBy);

  const originalAccountRows = useMemo(
    () => dataToDisplay.flatMap((group) => group.subRows),
    [dataToDisplay],
  );
  // The store is the source of truth for bookmarks, so a row bookmarked during
  // this session gets its date without waiting for a refetch.
  const bookmarksByAccount = usePricingBookmarksStore(
    (store) => store.bookmarks,
  );
  const bookmarkCount = usePricingBookmarksStore(selectBookmarkCount);

  const bookmarkedAt = useCallback(
    (row: PricingGroupedTableRow): string | null =>
      'restaurantAccountId' in row
        ? (bookmarksByAccount[
            bookmarkKey(row.variantPackUUID, row.restaurantUUID)
          ]?.bookmarkedAt ?? null)
        : null,
    [bookmarksByAccount],
  );

  const bookmarkedAtSortValue = useCallback(
    (row: Row<PricingGroupedTableRow>): number => {
      return latestBookmarkedAt(
        row.depth === 0
          ? row.subRows.map((subRow) => bookmarkedAt(subRow.original))
          : [bookmarkedAt(row.original)],
      );
    },
    [bookmarkedAt],
  );

  const editsByAccount = useMemo(() => {
    const map = new Map<string, PricingEdit>();
    edits.forEach((edit) => map.set(buildAccountKey(edit), edit));
    return map;
  }, [edits]);
  const mergedAccountRows = useMemo(
    () =>
      originalAccountRows.map((row) => {
        const edit = editsByAccount.get(buildAccountKey(row));
        return edit ? applyEditToRow(row, edit) : row;
      }),
    [editsByAccount, originalAccountRows],
  );

  // Unbookmarking drops rows from the tab, so the selection is pruned against
  // the rows still bookmarked.
  const availableKeys = useMemo(
    () => new Set(originalAccountRows.map(buildAccountKey)),
    [originalAccountRows],
  );

  const {
    clearSelection,
    isAllLocked,
    isAllSelected,
    isPartiallySelected,
    onFilteredRowsChange,
    selectedCount,
    selectedUUIDs: selectedKeys,
    toggleAllVisible,
    toggleSelection,
  } = useRowSelection<PricingGroupedTableRow>({
    availableIds: availableKeys,
    getRowId: getRowKey,
    isLocked: isRowLocked,
  });

  const handleFilteredRowsChange = useCallback(
    (rows: Row<PricingGroupedTableRow>[]) =>
      onFilteredRowsChange(rows.filter((row) => row.depth > 0)),
    [onFilteredRowsChange],
  );

  const toggleGroup = useCallback(
    (childKeys: string[]) => {
      const anyUnselected = childKeys.some((key) => !selectedKeys.has(key));
      childKeys.forEach((key) => {
        const isRowSelected = selectedKeys.has(key);
        if (anyUnselected ? !isRowSelected : isRowSelected) {
          toggleSelection(key);
        }
      });
    },
    [selectedKeys, toggleSelection],
  );

  useFocusEffect(
    useCallback(() => {
      refetch();
      clearSelection();
      resetUndoHistory(BOOKMARKED_SCOPE);
    }, [clearSelection, refetch, resetUndoHistory]),
  );

  const selectedPairs = useMemo<BookmarkPair[]>(
    () =>
      [...selectedKeys].map((key) => {
        const { restaurantUUID, variantPackUUID } = splitAccountKey(key);
        return {
          restaurant_id: restaurantUUID,
          variant_pack_id: variantPackUUID,
        };
      }),
    [selectedKeys],
  );

  // The Bookmarked tab spans every supplier the user can see, so the affected
  // suppliers come from the selected rows rather than a single page context.
  const selectedSupplierUUIDs = useMemo(
    () =>
      originalAccountRows
        .filter((row) => selectedKeys.has(buildAccountKey(row)))
        .map((row) => row.supplierUUID),
    [originalAccountRows, selectedKeys],
  );

  const {
    bookmarkAction,
    isNoteOpen: isBookmarkNoteOpen,
    onSaveNote: onSaveBookmarkNote,
  } = useBookmarkActionForPairs({
    canBookmarkItem: hasBookmarkingAccess,
    pageSource: PricingPageSource.BOOKMARKED,
    pairs: selectedPairs,
    supplierUUIDs: selectedSupplierUUIDs,
  });

  const {
    applyBulkInput,
    bulkEditField,
    bulkEditMode,
    bulkInputValue,
    bulkQuickActionStates,
    canApplyBulkInput,
    setBulkEditField,
    setBulkEditMode,
    setBulkInputValue,
  } = useBulkEditingCore({
    currencyCode,
    mergedRows: mergedAccountRows,
    pivot: BulkEditPivot.Pair,
    pricingEdits: edits,
    rows: originalAccountRows,
    scope: BOOKMARKED_SCOPE,
    selectedKeys,
  });

  const bulkQuickActions = useBulkQuickActions({
    bookmarkAction,
    bulkQuickActionStates,
  });

  const isBulkEditBarVisible = selectedCount > 0 && !timezoneLoading;

  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const searchInputRef = useRef<SearchInputRef>(null);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const [sorting, setSorting] = useState<SortingState>([
    { desc: true, id: ColumnId.BookmarkedDate },
  ]);
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );

  // Bounce off this page — to the hub, the section-neutral landing screen —
  // only once a settled read AND the store, the same source the sidebar entry
  // uses, both agree there is nothing to show.
  // Requiring the store guards against a cached list that predates the user's
  // newest bookmark; requiring the query guards against an optimistic unbookmark
  // navigating away before the delete is confirmed (a failed one rolls the store
  // back, but the cache — and this count — only drop on success).
  // Focus-gated: this screen stays mounted under pushed routes, so without it a
  // bulk unbookmark from the item screen would yank the user off that screen.
  useEffect(() => {
    if (isFocused && !loading && !error && count === 0 && bookmarkCount === 0) {
      navigation.navigate(SALES_PORTAL_ROUTES.PricingOverview);
    }
  }, [bookmarkCount, count, error, isFocused, loading, navigation]);

  // Regrouping replaces the parent rows, so it re-takes the snapshot too.
  const sortSnapshotSource = useMemo(
    () => ({ bookmarks, groupBy }),
    [bookmarks, groupBy],
  );
  const sortSnapshotRows = useMemo<PricingGroupedTableRow[]>(
    () => [...dataToDisplay, ...mergedAccountRows],
    [dataToDisplay, mergedAccountRows],
  );
  const { onSortingChange, resolveEditedRow } = usePricingSortSnapshot({
    keyOf: groupedRowKey,
    rows: sortSnapshotRows,
    setSorting,
    source: sortSnapshotSource,
  });

  // +1 for the Bookmarked date column, which only this table has.
  const regularColumnCount =
    PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT +
    1 +
    (isPeerRangeEnabled ? 1 : 0);
  const remainingPercentage = 100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE;
  const columnWidthPercentage = remainingPercentage / regularColumnCount;

  const columns = useMemo<ColumnDef<PricingGroupedTableRow, string>[]>(() => {
    return [
      columnHelper.display({
        cell: ({ row }) => {
          if (row.depth === 0) {
            const childKeys = row.subRows
              .filter((sub) => !(sub.original as PricingAccountRow).isLocked)
              .map((sub) => buildAccountKey(sub.original as PricingAccountRow));
            const allSelected =
              childKeys.length > 0 &&
              childKeys.every((key) => selectedKeys.has(key));
            const someSelected = childKeys.some((key) => selectedKeys.has(key));
            return (
              <View style={[styles.checkboxContainer, styles.checkbox]}>
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? 'indeterminate' : false
                  }
                  disabled={childKeys.length === 0}
                  onPress={() => toggleGroup(childKeys)}
                  size="$sm"
                  tabIndex={-1}
                />
              </View>
            );
          }

          const originalRow = row.original as PricingAccountRow;
          if (originalRow.isLocked) {
            return (
              <View style={styles.checkboxContainer}>
                <Tooltip placement="right">
                  <Tooltip.Trigger>
                    <LockIcon
                      height={16}
                      stroke={theme.sk.color.icon.tertiary}
                      width={16}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <Typography
                      color={theme.sk.color.text.ondark}
                      variant="paragraph-sm-600"
                    >
                      {i18n.t('pricing.table.locked_tooltip')}
                    </Typography>
                  </Tooltip.Content>
                </Tooltip>
              </View>
            );
          }

          const key = buildAccountKey(originalRow);
          const isRowSelected = selectedKeys.has(key);
          return (
            <View style={[styles.checkboxContainer, styles.checkbox]}>
              <Checkbox
                checked={isRowSelected}
                onPress={() => toggleSelection(key)}
                size="$sm"
                tabIndex={-1}
                testID={`pricing-select-row-${buildAccountKey(originalRow)}${isRowSelected ? '-checked' : ''}`}
              />
            </View>
          );
        },
        header: () => null,
        id: ColumnId.Checkbox,
        meta: {
          hideable: false,
        },
        size: PRICING_TABLE_LAYOUT.CHECKBOX_COLUMN_WIDTH,
      }),
      // Name
      columnHelper.accessor(
        (row) =>
          'subRows' in row ? row.groupName : leafLabelOf(row, groupBy).name,
        {
          cell: ({ row, getValue }) => {
            if (row.depth === 0) {
              const group = row.original as PricingGroupRow;
              const editsCount = group.subRows.filter((sub) =>
                editsByAccount.has(buildAccountKey(sub)),
              ).length;
              return (
                <View style={styles.parentNameWrapper}>
                  <View style={styles.nameWithBadgeWrapper}>
                    <PricingTableNameLink
                      name={getValue()}
                      onPress={() =>
                        navigateToEntity(group.groupBy, group.groupKey)
                      }
                    />
                    {editsCount > 0 ? (
                      <PricingEditsBadge editsCount={editsCount} />
                    ) : null}
                  </View>
                </View>
              );
            }

            const originalRow = row.original as PricingAccountRow;
            const leaf = leafLabelOf(originalRow, groupBy);

            return (
              <PricingTableNameLink
                name={leaf.name}
                onPress={() =>
                  navigateToEntity(leafDimensionOf(groupBy), leaf.key)
                }
              />
            );
          },
          header: () => <PricingTableHeader columnId={ColumnId.Name} />,
          id: ColumnId.Name,
          meta: {
            alwaysVisible: true,
            initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.Name),
          },
          minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
          sortingFn: 'alphanumeric',
        },
      ),
      // ID
      columnHelper.accessor(
        (row) =>
          'subRows' in row ? row.groupId : leafLabelOf(row, groupBy).id,
        {
          cell: ({ getValue }) => (
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md"
            >
              {getValue()}
            </Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.Id} />,
          id: ColumnId.Id,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.Id),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      // UOM
      columnHelper.accessor((row) => row.variantPackUOM, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.UOM} />,
        id: ColumnId.UOM,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.UOM),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Peer Range
      ...(isPeerRangeEnabled
        ? [
            columnHelper.display({
              cell: (info) => {
                if (info.row.depth === 0) {
                  return null;
                }
                const originalRow = info.row.original as PricingAccountRow;
                return (
                  <PricingPeerRangeCell
                    grossMarginPercentage={originalRow.grossMarginPercentage}
                    peerRange={originalRow.peerRange}
                    restaurantUUID={originalRow.restaurantUUID}
                    variantPackUUID={originalRow.variantPackUUID}
                  />
                );
              },
              enableSorting: false,
              header: () => (
                <PricingTableHeader columnId={ColumnId.PeerRange} />
              ),
              id: ColumnId.PeerRange,
              meta: {
                initialPercentWidth: columnWidthPercentage,
                label: getHeaderLabel(ColumnId.PeerRange),
              },
              ...COLUMN_WIDTH.M,
            }),
          ]
        : []),
      // Last GM%
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.GrossMargin, CostChangeValue.GrossMarginDelta],
            () => formatPercentage(getLastGrossMarginPercentage(row), 1, 1),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastMargin} />,
          id: ColumnId.LastMargin,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastMargin),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastGrossMarginPercentage(rowA.original) -
            getLastGrossMarginPercentage(rowB.original),
        },
      ),
      // Gross Margin %
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMargin], () =>
            formatPercentage(row.grossMarginPercentage, 1, 1),
          ),
        {
          cell: ({ row, getValue, ...context }) => {
            const value = getValue();
            if (row.depth === 0) {
              return <Typography variant="paragraph-md">{value}</Typography>;
            }

            const originalRow = row.original as PricingAccountRow;
            if (!hasEditablePricing(originalRow)) {
              return <PricingTableInputPlaceholder value={value} />;
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked}
                inputType={PricingEditField.GrossMarginPercentage}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatPercentage(
                  row.original.grossMarginPercentage,
                  1,
                )}
                originalRow={originalRow}
                pageSource={PricingPageSource.BOOKMARKED}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={row.id}
                scope={BOOKMARKED_SCOPE}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
                value={String(
                  parseFloat(row.original.grossMarginPercentage.toFixed(1)),
                )}
                variantPackUUID={row.original.variantPackUUID}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CurrentGrossMargin}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CurrentGrossMargin,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentGrossMargin),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossMarginPercentage - b.grossMarginPercentage;
          },
        },
      ),
      // Gross Margin % delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMarginDelta], () =>
            formatPercentage(
              roundPercentageToNearestTenth(row.grossMarginPercentageDelta),
              1,
              1,
            ),
          ),
        {
          cell: ({ row, getValue }) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              getValue(),
            )
              ? emptyDelta
              : resolveDelta(
                  roundPercentageToNearestTenth(
                    row.original.grossMarginPercentageDelta,
                  ),
                  (v) => formatPercentage(v, 1, 1),
                );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossMarginPercentageDelta"
                formatValue={(value) =>
                  formatPercentage(roundPercentageToNearestTenth(value), 1, 1)
                }
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-gross-margin-percentage-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossMarginPercentageDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossMarginPercentageDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossMarginPercentageDelta - b.grossMarginPercentageDelta;
          },
        },
      ),
      // Last Price
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () => formatCurrency(getLastPriceInMicros(row), currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastPrice} />,
          id: ColumnId.LastPrice,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastPriceInMicros(rowA.original) -
            getLastPriceInMicros(rowB.original),
        },
      ),
      // Current Price
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.Price], () =>
            formatCurrency(row.priceInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue, ...context }) => {
            const value = getValue();
            if (row.depth === 0) {
              return (
                <Typography
                  color={
                    row.index === 0 ? theme.sk.color.text.primary : undefined
                  }
                  testID={`price-group-${(row.original as PricingGroupRow).groupKey}`}
                  variant="paragraph-md"
                >
                  {value}
                </Typography>
              );
            }
            const priceInMicros = row.original.priceInMicros;
            const originalRow = row.original as PricingAccountRow;
            if (!hasEditablePricing(originalRow)) {
              return (
                <PricingTableInputPlaceholder
                  color={theme.sk.color.text.primary}
                  value={value}
                />
              );
            }
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={context.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked}
                inputType={PricingEditField.Price}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatCurrency(
                  priceInMicros,
                  currencyCode,
                )}
                originalRow={originalRow}
                pageSource={PricingPageSource.BOOKMARKED}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={row.id}
                scope={BOOKMARKED_SCOPE}
                tableRowIndex={getRenderedRowIndex(context, row.index)}
                value={value}
                variantPackUUID={originalRow.variantPackUUID}
              />
            );
          },
          header: () => <PricingTableHeader columnId={ColumnId.CurrentPrice} />,
          id: ColumnId.CurrentPrice,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.priceInMicros - b.priceInMicros;
          },
        },
      ),
      // Price delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.PriceDelta], () =>
            formatCurrency(row.priceDeltaInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue }) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              getValue(),
            )
              ? emptyDelta
              : resolveDelta(row.original.priceDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="priceDeltaInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-price-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.PriceDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.PriceDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.PriceDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.priceDeltaInMicros - b.priceDeltaInMicros;
          },
        },
      ),
      // Last Cost
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () => formatCurrency(getLastCostInMicros(row), currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.LastCost} />,
          id: ColumnId.LastCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.LastCost),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            getLastCostInMicros(rowA.original) -
            getLastCostInMicros(rowB.original),
        },
      ),
      // Current Cost
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.Cost], () =>
            formatCurrency(row.costInMicros, currencyCode),
          ),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.CurrentCost} />,
          id: ColumnId.CurrentCost,
          meta: {
            defaultHidden: true,
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentCost),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.costInMicros - rowB.original.costInMicros,
        },
      ),
      // Cost delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.CostDelta], () =>
            formatCurrency(row.costDeltaInMicros, currencyCode),
          ),
        {
          cell: (info) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              info.getValue(),
            )
              ? emptyDelta
              : resolveDelta(info.row.original.costDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                hasInvertedColor
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CostDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CostDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CostDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) =>
            rowA.original.costDeltaInMicros - rowB.original.costDeltaInMicros,
        },
      ),
      // Gross Profit
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossProfit], () =>
            formatCurrency(row.grossProfitInMicros, currencyCode),
          ),
        {
          cell: ({ row, getValue }) => {
            const value = getValue();
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={undefined}
                  hideDeltaAttributes
                  value={value}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossProfitInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                hideDeltaAttributes
                originalFormattedValue={value}
                originalRow={originalRow}
                originalValue={value}
                testID="original-gross-profit"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossProfit}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossProfit,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossProfit),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return a.grossProfitInMicros - b.grossProfitInMicros;
          },
        },
      ),
      // Weekly volume
      columnHelper.accessor((row) => row.quantity.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => (
          <PricingTableHeader
            columnId={ColumnId.WeeklyVolume}
            costChangeLookbackMode={costChangeLookbackMode}
            costChangeWeekAnchorDay={costChangeWeekAnchorDay}
            withTooltip
          />
        ),
        id: ColumnId.WeeklyVolume,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.WeeklyVolume),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (rowA, rowB) =>
          rowA.original.quantity - rowB.original.quantity,
      }),
      // Edits GP Impact
      columnHelper.accessor(
        (row) =>
          formatCurrency(
            roundMicrosToNearestWholeCent(row.editsGrossProfitImpactInMicros),
            currencyCode,
          ),
        {
          cell: ({ row }) => {
            const { direction, formattedValue } = resolveDelta(
              roundMicrosToNearestWholeCent(
                row.original.editsGrossProfitImpactInMicros,
              ),
              (v) => formatCurrency(v, currencyCode),
            );
            if (row.depth === 0) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="editsGrossProfitImpactInMicros"
                formatValue={(value) =>
                  formatCurrency(
                    roundMicrosToNearestWholeCent(value),
                    currencyCode,
                  )
                }
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-edits-gross-profit-impact"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.WeeklyEditsGrossProfitImpact}
              withTooltip
            />
          ),
          id: ColumnId.WeeklyEditsGrossProfitImpact,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.WeeklyEditsGrossProfitImpact),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original);
            const b = resolveEditedRow(rowB.original);
            return (
              a.editsGrossProfitImpactInMicros -
              b.editsGrossProfitImpactInMicros
            );
          },
        },
      ),
      // Bookmarked date
      columnHelper.accessor((row) => formatBookmarkedDate(bookmarkedAt(row)), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.BookmarkedDate} />,
        id: ColumnId.BookmarkedDate,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.BookmarkedDate),
        },
        ...COLUMN_WIDTH.M,
        // Group rows carry no bookmark of their own, so they sort by their
        // most recently bookmarked account.
        sortingFn: (rowA, rowB) =>
          bookmarkedAtSortValue(rowA) - bookmarkedAtSortValue(rowB),
      }),
    ];
  }, [
    bookmarkedAt,
    bookmarkedAtSortValue,
    columnWidthPercentage,
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
    currencyCode,
    editsByAccount,
    groupBy,
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    isPeerRangeEnabled,
    navigateToEntity,
    resolveEditedRow,
    selectedKeys,
    styles,
    theme,
    toggleGroup,
    toggleSelection,
  ]);

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingBookmarks,
    columns,
  );

  const accountEditCounts = useMemo(
    () =>
      edits.reduce<Record<string, number>>((acc, edit) => {
        const key = buildAccountKey(edit);
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {}),
    [edits],
  );

  const categoryOptions = useMemo(
    () => deriveCategoryOptions(mergedAccountRows),
    [mergedAccountRows],
  );

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingAccountRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    return {
      [ColumnId.LastMargin]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMargin, CostChangeValue.GrossMarginDelta],
            () => getLastGrossMarginPercentage(row),
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.LastMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.CurrentGrossMargin]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMargin],
            () => row.grossMarginPercentage,
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.CurrentGrossMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.GrossMarginPercentageDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossMarginDelta],
            () => row.grossMarginPercentageDelta,
          ),
        affix: '%',
        title: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.LastPrice]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () => getLastPriceInMicros(row) / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.LastPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CurrentPrice]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price],
            () => row.priceInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.PriceDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.PriceDelta],
            () => row.priceDeltaInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        filterPillMetric: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Price, CostChangeValue.PriceDelta],
            () =>
              percentOfPrevious(
                row.priceDeltaInMicros,
                getLastPriceInMicros(row),
              ),
          ),
        title: getHeaderLabel(ColumnId.PriceDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.LastCost]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () => getLastCostInMicros(row) / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.LastCost),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CurrentCost]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost],
            () => row.costInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentCost),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CostDelta]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.CostDelta],
            () => row.costDeltaInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        filterPillMetric: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.Cost, CostChangeValue.CostDelta],
            () =>
              percentOfPrevious(
                row.costDeltaInMicros,
                getLastCostInMicros(row),
              ),
          ),
        title: getHeaderLabel(ColumnId.CostDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.GrossProfit]: {
        accessor: (row) =>
          valueOrNull(
            row,
            [CostChangeValue.GrossProfit],
            () => row.grossProfitInMicros / MICRO_CONVERSION,
          ),
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.GrossProfit),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.WeeklyVolume]: {
        accessor: (row) => row.quantity,
        affix: 'u',
        title: getHeaderLabel(ColumnId.WeeklyVolume),
        unit: PricingFilterUnit.Unit,
      },
      [ColumnId.WeeklyEditsGrossProfitImpact]: {
        accessor: (row) =>
          row.editsGrossProfitImpactInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.WeeklyEditsGrossProfitImpact),
        unit: PricingFilterUnit.Currency,
      },
    };
  }, [currencyCode]);

  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );

  const sections = useMemo(
    () =>
      buildPricingFilterSections({
        categoryOptions,
        columnIds: filterColumnIds,
        editCounts: {
          counts: accountEditCounts,
          getRowId: (row) => buildAccountKey(row),
        },
        peerAverage: isPeerRangeEnabled
          ? {
              getCurrentGrossMargin: (row) => row.grossMarginPercentage,
              getPeerRange: (row) => row.peerRange,
            }
          : undefined,
        rangeColumnConfigs,
        rows: mergedAccountRows,
        timezone,
      }),
    [
      accountEditCounts,
      categoryOptions,
      filterColumnIds,
      isPeerRangeEnabled,
      mergedAccountRows,
      rangeColumnConfigs,
      timezone,
    ],
  );

  const filters = usePricingFilters(PricingFilterScope.Bookmarked, sections);
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params ?? {},
    sections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  useEffect(() => {
    filters.pruneSections(sections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, filters.pruneSections]);

  const { pills: filterPills, ranges: filterRanges } = filters;

  const globalFilter = useMemo<PricingReviewFilterValue | undefined>(() => {
    if (debouncedSearchText.length === 0 && !filters.hasActiveFilters) {
      return undefined;
    }

    if (!filters.hasActiveFilters) {
      return { text: debouncedSearchText };
    }

    const matchesFilters = buildPricingFilterPredicate(
      { pills: filterPills, ranges: filterRanges },
      sections,
    );

    return {
      matches: (row) =>
        matchesFilters(resolveEditedRow(row.original) as PricingAccountRow),
      text: debouncedSearchText,
    };
  }, [
    debouncedSearchText,
    filterPills,
    filterRanges,
    filters.hasActiveFilters,
    resolveEditedRow,
    sections,
  ]);

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [clearSearch, debouncedSearchText, filters]);

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<PricingGroupedTableRow>
  >(
    ({ headerGroup }) => (
      <PricingTableHeaderCells
        headerCellStyle={styles.headerCell}
        headerGroup={headerGroup}
        isLeftAlignedColumn={isLeftAlignedColumn}
        key={headerGroup.id}
        sorting={sorting}
      />
    ),
    [sorting, styles.headerCell],
  );

  const renderRow = useCallback<RenderRowCallback<PricingGroupedTableRow>>(
    ({ index, row }) => {
      if (row.depth > 0) {
        const originalRow = row.original as PricingAccountRow;
        return (
          <PricingItemDetailsTableRowContainer
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            hasGreyBackground
            index={index}
            isSelected={selectedKeys.has(buildAccountKey(originalRow))}
            key={row.id}
            row={row as Row<PricingAccountRow>}
          />
        );
      }
      const childKeys = row.subRows.map((sub) =>
        buildAccountKey(sub.original as PricingAccountRow),
      );
      const selectedInGroup = childKeys.reduce(
        (total, key) => total + (selectedKeys.has(key) ? 1 : 0),
        0,
      );
      return (
        <PricingBookmarkGroupRow
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          index={index}
          isDeltaColumn={isDeltaColumn}
          isLeftAlignedColumn={isLeftAlignedColumn}
          key={row.id}
          row={row}
          selectionSignature={`${selectedInGroup}/${childKeys.length}`}
          testID={`pricing-table-row-${row.index}`}
        />
      );
    },
    [columnOrder, columnVisibility, selectedKeys],
  );

  // Fresh objects here would churn the table context and FlashList each render.
  const cellPadding = useMemo(
    () => [
      theme.sk.spacing.spaceMedium,
      ...Array(columns.length - 1).fill(theme.sk.spacing.spaceSmall),
    ],
    [columns.length, theme.sk.spacing.spaceMedium, theme.sk.spacing.spaceSmall],
  );
  const bodyContainerStyle = useMemo(
    () => ({
      paddingBottom: isBulkEditBarVisible
        ? BAR_CONTROL_HEIGHT + 2 * theme.sk.spacing.spaceLarge
        : theme.sk.spacing.spaceLarge,
    }),
    [isBulkEditBarVisible, theme.sk.spacing.spaceLarge],
  );

  const loadingSkeleton = (
    <View
      style={styles.loadingContent}
      testID="pricing-bookmarks-loading-skeleton"
    >
      <SearchSkeleton />
      <PricingTableSkeleton />
    </View>
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          <View
            style={[
              styles.topBarActions,
              panel.isOpen && styles.actionsWithPanelOpen,
            ]}
          >
            {hasUndoHistory ? <PricingUndoButton onPress={handleUndo} /> : null}
            {edits.length > 0 ? (
              <PricingReviewButton
                onPress={() =>
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingManagementReview,
                  )
                }
              />
            ) : null}
          </View>
        }
        leadingActions={
          <PricingBreadcrumbs
            secondaryBreadcrumb={i18n.t('pricing.bookmarks.sidenav_title')}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={sections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            loadingSkeleton
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await refetch()}
                  variant={ButtonVariant.Secondary}
                  width={120}
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={loadingSkeleton}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingBookmarks}
                    />
                    <PricingGroupByToggleButton
                      groupBy={groupBy}
                      onPress={toggleGroupBy}
                    />
                  </>
                }
              >
                <SearchInput
                  onSearchChange={setSearchText}
                  placeholderText={i18n.t('pricing.search_placeholder')}
                  ref={searchInputRef}
                  searchText={searchText}
                />
              </PricingSearchBar>
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  bodyContainerStyle={bodyContainerStyle}
                  cellPadding={cellPadding}
                  columns={columns}
                  data={dataToDisplay}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={null}
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedHeaderHeight={PRICING_TABLE_LAYOUT.HEADER_HEIGHT}
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  filterFromLeafRows
                  getSubRows={(row) =>
                    'subRows' in row ? row.subRows : undefined
                  }
                  globalFilterFn={pricingReviewGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: {
                      left: [ColumnId.Checkbox, ColumnId.Name],
                    },
                  }}
                  isActive
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onFilteredRowsChange={handleFilteredRowsChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  sectionHeader={
                    dataToDisplay.length > 0 ? (
                      <PricingSelectAllRow
                        disabled={isAllLocked}
                        isAllSelected={isAllSelected}
                        isPartiallySelected={isPartiallySelected}
                        label={i18n.t('pricing.table.select_all')}
                        onToggleAll={toggleAllVisible}
                      />
                    ) : null
                  }
                  state={{
                    columnOrder,
                    columnVisibility,
                    expanded: true,
                    globalFilter,
                    sorting,
                  }}
                />
                {isBulkEditBarVisible ? (
                  <PricingBulkEditBottomBar
                    canApply={canApplyBulkInput}
                    editField={bulkEditField}
                    editMode={bulkEditMode}
                    isBookmarkNoteOpen={isBookmarkNoteOpen}
                    onApply={applyBulkInput}
                    onEditFieldChange={setBulkEditField}
                    onEditModeChange={setBulkEditMode}
                    onInputValueChange={setBulkInputValue}
                    onSaveBookmarkNote={onSaveBookmarkNote}
                    quickActions={bulkQuickActions}
                    selectedCount={selectedCount}
                    selectedCountLabel={i18n.t(
                      selectedCount === 1
                        ? 'pricing.bulk_edit.customer_count.singular'
                        : 'pricing.bulk_edit.customer_count.plural',
                      { count: selectedCount },
                    )}
                    value={bulkInputValue}
                  />
                ) : null}
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  checkbox: {
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: 14,
  },
  checkboxContainer: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    // Negative margin widens the checkbox's interaction target.
    marginHorizontal:
      -theme.sk.spacing.spaceLarge - theme.sk.spacing.spaceXsmall,
    marginVertical: -theme.sk.spacing.spaceSMedium,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  headerCell: {
    alignItems: 'flex-end',
    height: PRICING_TABLE_LAYOUT.HEADER_HEIGHT,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  nameWithBadgeWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
  },
  parentNameWrapper: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
  topBarActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
